// core
import {Component, HostListener, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

// ng-bootstrap UI
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';

// components
import {TargetGroupModal} from './modals/target-group-modal/target-group-modal.component';
import {RolesModalComponent} from './modals/roles-modal/roles-modal.component';
import {
  CustomerByCustomerNumberModal
} from 'app/user/slx-admin-user-details/modals/customer-by-customer-number-modal/customer-by-customer-number-modal.component';

// service
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {UserRegistrationServiceService} from '../../user-registration/service/user-registration-service.service';
import {CustomerService} from '../../customer/services/customer-service.service';
import {UserDetailService} from '../service/user-detail.service';
import {RolesService} from '../../role/roles.service';
import {ErrorMessageService} from '../../core/alert-message/alert-message.service';

// utils
import {RolesUtils} from '../../utils/roles.utils';
import {UtilsService} from '../../utils/utils.service';

// model
import {User} from '../models/user.model';
import {EditableUserAttributes} from '../models/editableUserAttributes.model';

// constants
import {AppConstants} from '../../utils/constants';
import {CustomerModel} from '../../customer/models/customer-model';
import {UnsavedChangesComponent} from '../../shared/unsaved-changes-component';
import {take} from "rxjs/operators";



@Component({
  selector: 'app-slx-admin-user-details',
  templateUrl: './slx-admin-user-details.component.html',
  styleUrls: ['./slx-admin-user-details.component.scss'],
  standalone: false
})
export class SlxAdminUserDetailsComponent implements OnInit, UnsavedChangesComponent {

  // modals
  public targetGroupModalRef: BsModalRef;
  public rolesModalRef: BsModalRef;
  public customerByCustomerNumberModalRef: BsModalRef;

  private readonly slxUserForm: UntypedFormGroup;
  private user: User = new User();

  language: string;

  // form status
  private currentMode: string;
  private listOfTargetGroups: Array<any> = new Array<any>();
  public englishTitles;
  private listOfTitles: Array<any> = new Array<any>();
  public languages;
  public countries;
  private listOfLanguages: Array<any> = new Array<any>();
  private listOfCountries: Array<any> = new Array<any>();
  public postalCodeRequired = false;

  public whatLanguage;

  private homePath = AppConstants.homePage;

  private currentCustomer: CustomerModel;

  // these are the assigned and assignable roles combined
  private roleOptions: Array<any> = new Array<any>();

  private readonly = false;
  private emailAddressVerified = false;

  private editableAttributes: EditableUserAttributes;

  public loading = false;

  private askForNavigationConfirmation = true;

  constructor(
    private _utilsService: UtilsService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _userDetailsService: UserDetailService,
    private _customerService: CustomerService,
    private errorMessageService: ErrorMessageService,
    private _userRegistrationService: UserRegistrationServiceService,
    private _translateService: TranslateService,
    private _modalService: BsModalService,
    private _rolesService: RolesService,
    private _rolesUtils: RolesUtils,
  ) {

    _translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.whatLanguage = event.lang;
      this.listOfLanguages = this._utilsService.initializeSelectOrMultiselect(this.languages);
      this.listOfCountries = this._utilsService.initializeSelectOrMultiselect(this.countries);
      this.listOfTitles = this._utilsService.initializeSelectOrMultiselect(this.englishTitles);
    });

    this.slxUserForm = this._formBuilder.group({
      company: [
        {
          value: '',
          disabled: true
        }
      ],
      customerNumber: [
        {
          value: '',
          disabled: true
        }
      ],
      targetGroup: [
        {
          value: '',
          disabled: true
        },
        Validators.required
      ],
      contactPerson: [
        {
          value: '',
          disabled: true
        }
      ],
      crmContactId: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.minLength(36),
          Validators.maxLength(36)
        ])
      ],
      title: [
        {
          value: null,
          disabled: false
        }
      ],
      firstName: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20)
        ])
      ],
      lastName: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(40)
        ])
      ],
      email: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.pattern(this._utilsService.getEmailRegexPattern()),
          Validators.maxLength(50)
        ])
      ],
      birthDate: [
        {
          value: null,
          disabled: false
        }
      ],
      phone: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.pattern(this._utilsService.getPhoneNumberRegex()),
          Validators.maxLength(20)
        ])
      ],
      language: [
        {
          value: '',
          disabled: false
        },
        Validators.required
      ],
      countryCode: [
        {
          value: '',
          disabled: false
        },
        Validators.required
      ],
      postalCode: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.maxLength(10),
          Validators.minLength(2),
          Validators.pattern(this._utilsService.getPostalCodeRegex())
        ])
      ],
      selectedRoles: [
        {
          value: [],
          disabled: true
        }
      ],
      sapSalesOrganization: [
        {
          value: '',
          disabled: true
        }
      ],
      sapCompanyCode: [
        {
          value: '',
          disabled: true
        }
      ],
      sapPlant: [
        {
          value: '',
          disabled: true
        }
      ],
      sapUserName: [
        {
          value: '',
          disabled: true
        }
      ],
      active: [
        {
          value: true,
          disabled: false
        }
      ],
      confirmedByAdmin: [
        {
          value: false,
          disabled: false
        }
      ],
      emailVerified: [
        {
          value: false,
          disabled: true
        }
      ],
      customerAdmin: [
        {
          value: false,
          disabled: false
        }
      ],
      wantsNewsletter: [
        {
          value: false,
          disabled: true
        }
      ],
    });
  }

  ngOnInit() {
    this.currentMode = this._activatedRoute.snapshot.queryParams['type'];

    this._userRegistrationService.getSalutations().subscribe((response) => {
      this.englishTitles = response.filter((title) =>
        !AppConstants.disabledSalutationCodes.includes(title)
      );
      this.listOfTitles = this._utilsService.initializeListOfSalutations(this.englishTitles);
    });

    this._userRegistrationService.getLanguages().subscribe((response) => {
      this.languages = response;
      this.listOfLanguages = this._utilsService.initializeSelectOrMultiselect(response);
    });
    this._userRegistrationService.getCountries().subscribe((response) => {
      this.countries = response;
      this.listOfCountries = this._utilsService.initializeSelectOrMultiselect(response);
    });
    this._userRegistrationService.getTargetGroups().subscribe((response) => {
      this.listOfTargetGroups = this._utilsService.initializeSelectOrMultiselect(response);
    });

    if (this.isEditMode()) {
      const userId = this._activatedRoute.snapshot.data['user'].id;
      this._userDetailsService.getUserById(userId).subscribe(userResponse => {
        this.user = userResponse;
        this.emailAddressVerified = userResponse.emailVerified;
        this._initFormWithExistingUserData();
        this._initEnabledAttributes();

        if (!this.editableAttributes.personal) {
          this.setupPostalCodeValidators(false);
          this.disablePersonalControls();
        } else {
          this.setupPostalCodeValidators(userResponse.targetGroup === AppConstants.architectPlanner);
        }
        if (!this.editableAttributes.customer) {
          this.disableCustomerControls();
        }
        if (!this.editableAttributes.accountControl) {
          this.disableAccountControlControls();
        }
        if (!this.editableAttributes.roles) {
          this.disableRoleControls();
        }
        if (!this.editableAttributes.sap) {
          this.disableSapControls();
        }
        if (!this.editableAttributes.crm) {
          this.disableCrmControls();
        }

        if (this.user.confirmed) {
          this.slxUserForm.controls['confirmedByAdmin'].disable();
        }

        const userRoles = userResponse.roles.map(({name: key, ...rest}) => ({key, ...rest}));
        this.roleOptions = this._rolesUtils._prepareRolesSelection(userRoles);
        this._setSelectedRoles(userRoles);

        if (this.user.customerNumber) {
          this._customerService.getCustomerByCustomerNumber(this.user.customerNumber).pipe(take(1)).subscribe({
            next: (data) => {
              this.currentCustomer = data;
              this._fillCustomer(this.currentCustomer);

              const customerRoles = data.roles.filter((role) => role.roleType === "User");
              this.roleOptions = this._rolesUtils._prepareRolesSelection(
                this._rolesUtils._mergeResultsWithoutDuplicates([...userRoles], customerRoles)
              );
            },
            error: () => {
              this.askForNavigationConfirmation = false;
              this.cancelUserCreation();
            }
          });
        }
      });
    } else {
      setTimeout(() => {
        this._beginAddCustomerFlow();
      }, 0)
    }
  }

  public getSlxUserForm() {
    return this.slxUserForm;
  }

  public getUser() {
    return this.user;
  }

  public getListOfTargetGroups() {
    return this.listOfTargetGroups;
  }

  public getListOfTitles() {
    return this.listOfTitles;
  }

  public getListOfLanguages() {
    return this.listOfLanguages;
  }

  public getListOfCountries() {
    return this.listOfCountries;
  }

  public getHomePath() {
    return this.homePath;
  }

  public isEditMode(): boolean {
    return this.currentMode === AppConstants.EDIT_MODE;
  }

  public isCreateMode(): boolean {
    return this.currentMode === AppConstants.CREATE_MODE;
  }

  public getRoleOptions() {
    return this.roleOptions;
  }

  public isUserReadonly() {
    return this.readonly;
  }

  public getEditableAttributes() {
    return this.editableAttributes;
  }

  public isEmailAddressVerified() {
    return this.emailAddressVerified;
  }

  public isDirty() {
    return this.isEditMode() && this.slxUserForm.dirty && this.askForNavigationConfirmation;
  }

  @HostListener('window:beforeunload')
  canUnloadWindow() {
    return !this.slxUserForm.dirty || !this.isEditMode();
  }

  public cancelUserCreation() {
    this._router.navigate([AppConstants.slxAdminManageUsers]);
  }

  public save() {
    this.loading = true;
    let user: User = this._getUserFromFormValues(this.slxUserForm.getRawValue());
    user.roles = [];
    if (this.slxUserForm.controls.selectedRoles.value) {
      this.slxUserForm.controls.selectedRoles.value.forEach((val) => {
        user.roles.push({id: val})
      });
    }
    this._userDetailsService.saveOrCreateUser(user).subscribe(
      () => {
        this.loading = false;
        this.askForNavigationConfirmation = false;
        this._router.navigate([AppConstants.slxAdminManageUsers]);
      },
      (error) => {
        this.loading = false;
        // OpenErrorModal
        this.errorMessageService.openErrorModal(error);
      });
  }

  public manageUsers() {
    this._router.navigate(
      [AppConstants.slxAdminManageUsers],
      {queryParams: {customerNumber: this.slxUserForm.controls['customerNumber'].value}}
    );
  }

  public manageCustomer() {
    this._router.navigate(
      ['/' + AppConstants.editCustomer, this.slxUserForm.controls['customerNumber'].value],
      {queryParams: {type: 'edit'}});
  }

  public addCustomer() {
    this.changeCustomer();
  }

  private _beginAddCustomerFlow() {
    this.customerByCustomerNumberModalRef = this._modalService.show(CustomerByCustomerNumberModal);
    this.customerByCustomerNumberModalRef.content.currentFlow = this.currentMode;
    this.customerByCustomerNumberModalRef.content.callBack = this.modalCallBack;
  }

  public changeCustomer() {
    this.customerByCustomerNumberModalRef = this._modalService.show(CustomerByCustomerNumberModal);
    this.customerByCustomerNumberModalRef.content.currentFlow = this.currentMode;
    this.customerByCustomerNumberModalRef.content.callBack = this.modalCallBack;
  }

  public removeCustomer() {
    const isCustomerObjectPresent = this.slxUserForm.controls['customerNumber'].value != null;
    if (isCustomerObjectPresent) {
      this.slxUserForm.controls['selectedRoles'].reset();
      this.slxUserForm.controls['customerAdmin'].reset();
    }
    this.slxUserForm.controls['company'].reset();
    this.slxUserForm.controls['customerNumber'].reset();
    this.slxUserForm.controls['contactPerson'].reset();
    this.currentCustomer = null;
  }

  public changeTargetGroup() {
    if (this.slxUserForm.controls['targetGroup'].value && (this.currentCustomer || this.slxUserForm.controls['customerNumber'].value)) {
      return;
    } else {
      this.targetGroupModalRef = this._modalService.show(TargetGroupModal);
      this.targetGroupModalRef.content.currentFlow = this.currentMode;
      this.targetGroupModalRef.content.callBack = this.modalCallBack;
      this.targetGroupModalRef.content.customerName = this.slxUserForm.controls['company'].value;
      this.targetGroupModalRef.content.preselectedTargetGroup = this.slxUserForm.controls['targetGroup'].value;
      this.targetGroupModalRef.content.userId = this._activatedRoute.snapshot.data['user']?.id;
      this.postalCodeRequired = this.slxUserForm.controls['targetGroup'].value
    }
  }

  public changeRoles() {
    const initialState = {
      currentFlow: this.currentMode,
      customerNumber: this.slxUserForm.controls['customerNumber'].value,
      callBack: this.modalCallBack,
      preselectedTargetGroup: this.slxUserForm.controls['targetGroup'].value,
      preselectedRoles: this.slxUserForm.controls['selectedRoles'].value,
      listOfRoles: this.roleOptions,
      userId: this._activatedRoute.snapshot.data['user']?.id
    };
    this.rolesModalRef = this._modalService.show(RolesModalComponent, {initialState});
  }

  public deleteUser(confirmation) {
    if (confirmation) {
      this.askForNavigationConfirmation = false;
      this.loading = true;
      this._userDetailsService.deleteUser(this.user.id).pipe(take(1)).subscribe({
        next: (result) => {
          this.loading = false;
          this.askForNavigationConfirmation = false;
          // show success message
          this.errorMessageService.openConfirmationModal(result);
          // navigate to manage users page
          this._router.navigate(['/' + AppConstants.slxAdminManageUsers]);
        },
        error: (error) => {
          this.loading = false;
          this.errorMessageService.openErrorModal(error);
        }
      });
    }
  }

  public sendVerificationLink() {
    const userId = this._activatedRoute.snapshot.data['user'].id;

    this._userDetailsService.sendVerificationEmail(userId).pipe(take(1)).subscribe({
      next: () => {
        this.errorMessageService.openConfirmationModal('Verification link sent');
      }, error: (error) => {
        this.errorMessageService.openErrorModal(error);
      }
    });
  }

  private _fillCustomer(customerData?: CustomerModel) {
    if (customerData) {
      this.slxUserForm.controls['customerNumber'].setValue(customerData.customerNumber);
      this.slxUserForm.controls['company'].setValue(customerData.customerName);
      this.slxUserForm.controls['contactPerson'].setValue(this._utilsService.concatFirstNameWithLastName(
        customerData.contactPersonFirstName,
        customerData.contactPersonLastName)
      );
      this.slxUserForm.controls['targetGroup'].setValue(customerData.customerCategory); // JM 27.10.2020 | I think this is not necessary
    }
  }

  private _setSelectedRoles(roles: Array<any>) {
    if (roles) {
      const result: Array<string> = new Array<string>();
      roles.forEach(role => {
        result.push(role.id || role.roleId)
      });
      this.slxUserForm.controls.selectedRoles.setValue(result);
    }
  }

  private _getUserFromFormValues(formValues: any): User {
    let result: User = new User();
    result.id = this.user.id;
    result.sapSalesOrganization = formValues.sapSalesOrganization;
    result.sapCompanyCode = formValues.sapCompanyCode;
    result.sapPlant = formValues.sapPlant;
    result.sapUserName = formValues.sapUserName;
    result.salutation = formValues.title;
    result.crmContactId = (formValues.crmContactId && formValues.crmContactId.trim() !== '') ? formValues.crmContactId : this.user.crmContactId;
    result.firstName = formValues.firstName;
    result.lastName = formValues.lastName;
    result.email = formValues.email;
    if (formValues.birthDate) {
      result.birthDate = this._utilsService.convertNgbDateToIsoDateString(formValues.birthDate);
    }
    result.phone = formValues.phone;
    result.language = formValues.language;
    result.countryCode = formValues.countryCode;
    result.active = formValues.active;
    result.confirmed = formValues.confirmedByAdmin;
    result.customerAdmin = formValues.customerAdmin;
    result.wantsNewsletter = formValues.wantsNewsletter;
    result.customerNumber = formValues.customerNumber;
    result.targetGroup = formValues.targetGroup;
    result.customerName = formValues.company;
    result.postalCode = formValues.postalCode;
    return result;
  }

  /**
   * @description Callback function for modals
   * @param modalName
   * @param data
   */
  public modalCallBack = (modalName, data) => {
    switch (modalName) {
      /* Select customer by customer number */
      case AppConstants.customerByCustomerNumberModal:
        this.slxUserForm.controls['company'].setValue(data.company);
        this.slxUserForm.controls['customerNumber'].setValue(data.customerNumber);
        this.slxUserForm.controls['targetGroup'].setValue(data.targetGroup);
        this.slxUserForm.controls['contactPerson'].setValue(data.contactPerson);
        this.slxUserForm.controls['selectedRoles'].reset();
        this.slxUserForm.controls['customerAdmin'].reset();

        const customerInitialState = {
          currentFlow: this.currentMode,
          callBack: this.modalCallBack,
          customerNumber: data.customerNumber
        };
        this.rolesModalRef = this._modalService.show(RolesModalComponent, {initialState: customerInitialState});
        break;
      /* Select Target Group */
      case AppConstants.targetGroupModal:
        this.slxUserForm.controls['targetGroup'].setValue(data.targetGroup);
        this.slxUserForm.controls['selectedRoles'].reset();

        // Allow Angular change detection to process form updates before opening modal
        setTimeout(() => {
          const targetGroupInitialState = {
            currentFlow: this.currentMode,
            callBack: this.modalCallBack,
            preselectedTargetGroup: data.targetGroup,
            userId: data.userId
          };
          this.rolesModalRef = this._modalService.show(RolesModalComponent, {initialState: targetGroupInitialState});
        }, 0);

        this.setupPostalCodeValidators(data.targetGroup === AppConstants.architectPlanner);
        break;
      /* Select User Roles */
      case AppConstants.rolesModal:
        this.roleOptions = data.roleOptions;
        this.slxUserForm.controls.selectedRoles.setValue(data.roles);
        this.slxUserForm.controls['confirmedByAdmin'].setValue(true);
        break;
    }
  };

  private _initFormWithExistingUserData() {
    this.slxUserForm.controls['customerNumber'].setValue(this.user.customerNumber);
    this.slxUserForm.controls['sapSalesOrganization'].setValue(this.user.sapSalesOrganization);
    this.slxUserForm.controls['sapCompanyCode'].setValue(this.user.sapCompanyCode);
    this.slxUserForm.controls['sapPlant'].setValue(this.user.sapPlant);
    this.slxUserForm.controls['sapUserName'].setValue(this.user.sapUserName);
    if (this.user.salutation && !this.englishTitles.includes(this.user.salutation)) {
      this.englishTitles.push(this.user.salutation);
      this.listOfTitles = this._utilsService.initializeListOfSalutations(this.englishTitles);
    }
    this.slxUserForm.controls['title'].setValue(this.user.salutation);
    this.slxUserForm.controls['crmContactId'].setValue(this.user.crmContactId);
    this.slxUserForm.controls['firstName'].setValue(this.user.firstName);
    this.slxUserForm.controls['lastName'].setValue(this.user.lastName);
    this.slxUserForm.controls['email'].setValue(this.user.email);
    if (this.user.birthDate) {
      this.slxUserForm.controls['birthDate'].setValue(this._utilsService.convertIsoDateStringToNgbDate(this.user.birthDate));
    }
    this.slxUserForm.controls['phone'].setValue(this.user.phone);
    this.slxUserForm.controls['language'].setValue(this.user.language);
    this.slxUserForm.controls['countryCode'].setValue(this.user.countryCode);
    this.slxUserForm.controls['active'].setValue(this.user.active);
    this.slxUserForm.controls['confirmedByAdmin'].setValue(this.user.confirmed);
    this.slxUserForm.controls['emailVerified'].setValue(this.user.emailVerified);
    this.slxUserForm.controls['customerAdmin'].setValue(this.user.customerAdmin);
    this.slxUserForm.controls['wantsNewsletter'].setValue(this.user.wantsNewsletter);
    this.slxUserForm.controls['company'].setValue(this.user.customerName);
    this.slxUserForm.controls['targetGroup'].setValue(this.user.targetGroup);
    this.slxUserForm.controls['postalCode'].setValue(this.user.postalCode)
  }

  private _initEnabledAttributes() {
    this.editableAttributes = this.user.editableAttributes;
    this.readonly = !this.editableAttributes.personal && !this.editableAttributes.customer
      && !this.editableAttributes.accountControl && !this.editableAttributes.roles && !this.editableAttributes.sap
      && !this.editableAttributes.crm;
  }

  private disableAllControls() {
    this.disablePersonalControls();
    this.disableCustomerControls();
    this.disableAccountControlControls();
    this.disableRoleControls();
    this.disableSapControls();
    this.disableCrmControls();
  }

  private disablePersonalControls() {
    this.slxUserForm.controls['title'].disable();
    this.slxUserForm.controls['firstName'].disable();
    this.slxUserForm.controls['lastName'].disable();
    this.slxUserForm.controls['email'].disable();
    this.slxUserForm.controls['birthDate'].disable();
    this.slxUserForm.controls['phone'].disable();
    this.slxUserForm.controls['language'].disable();
    this.slxUserForm.controls['countryCode'].disable();
    this.slxUserForm.controls['targetGroup'].disable();
    this.slxUserForm.controls['wantsNewsletter'].disable();
    this.slxUserForm.controls['postalCode'].disable();
  }

  private disableCustomerControls() {
    this.slxUserForm.controls['customerNumber'].disable();
    this.slxUserForm.controls['company'].disable();
  }

  private disableAccountControlControls() {
    this.slxUserForm.controls['active'].disable();
    this.slxUserForm.controls['confirmedByAdmin'].disable();
  }

  private disableRoleControls() {

    this.slxUserForm.controls['customerAdmin'].disable();
  }

  private disableSapControls() {
    this.slxUserForm.controls['sapSalesOrganization'].disable();
    this.slxUserForm.controls['sapCompanyCode'].disable();
    this.slxUserForm.controls['sapPlant'].disable();
    this.slxUserForm.controls['sapUserName'].disable();
  }

  private disableCrmControls() {
    this.slxUserForm.controls['crmContactId'].disable();
  }

  // set standard roles of customer in FE if user was confirmed
  public setStandardRolesOfCustomer(e) {
    if (e.target.checked) {
      let user: User = this._getUserFromFormValues(this.slxUserForm.getRawValue());
      if (user.customerNumber) {
        this._rolesService.getAssignedStandardCustomerRoles(user.customerNumber).subscribe((standardRoles) => {
          this.roleOptions = this._rolesUtils._prepareRolesSelection(standardRoles);
          this._setSelectedRoles(standardRoles)
        });
      } else {
        // Confirmed users without customer should get default roles of target group
        this._rolesService.getRolesByTargetGroupAndRoleType(user.targetGroup, 'Standard').subscribe(standardRolesOfTargetGroup => {
          this.roleOptions = this._rolesUtils._prepareRolesSelection(standardRolesOfTargetGroup);

          this._setSelectedRoles(standardRolesOfTargetGroup)
        });
      }
    } else {
      this.roleOptions = this._rolesUtils._prepareRolesSelection(new Array<any>());
      this._setSelectedRoles(new Array<any>())
    }

  }

  // get description for disabled "Change roles"-button
  public getToolTip() {
    return (!this.getSlxUserForm().controls['confirmedByAdmin'].value && this.getUser().customerId) ? this._translateService.instant('If a user is not confirmed, you can not change the roles. Please confirm the user in order to change the roles.') : false
  }

  private setupPostalCodeValidators(required) {
    this.slxUserForm.controls.postalCode.setValidators([]);
    if (required) {
      this.postalCodeRequired = true;
      this.slxUserForm.controls.postalCode.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this._utilsService.getPostalCodeRegex())
      ]);
    } else {
      this.postalCodeRequired = false;
      this.slxUserForm.controls.postalCode.setValidators([
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this._utilsService.getPostalCodeRegex())
      ]);
    }
    this.slxUserForm.controls.postalCode.markAsUntouched();
    this.slxUserForm.controls.postalCode.updateValueAndValidity();
  }

}
